import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MessageCircle, XCircle } from "lucide-react";
import { API, useAuth } from "@/App";

const brl = (n) => (n ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
const WHATSAPP_LINK = (msg) => `https://wa.me/5547999711358?text=${encodeURIComponent(msg)}`;

export default function ReservationSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let count = 0;
    const MAX = 15;

    const poll = async () => {
      count += 1;
      setAttempts(count);
      try {
        const { data: d } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); return; }
        if (d.status === "expired") { setStatus("expired"); return; }
        if (count >= MAX) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (count >= MAX) { setStatus("error"); return; }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="reservation-success-page">
      <div className="pm-card pm-glow-ring max-w-lg w-full p-8">
        {(status === "polling" || status === "timeout") && (
          <>
            <div className="pm-label mb-2">// Confirmando pagamento</div>
            <div className="flex items-center gap-3 mt-6">
              <Loader2 className="animate-spin text-[color:var(--pm-neon)]" />
              <span className="text-white/80">
                {status === "polling"
                  ? `Aguarde... tentativa ${attempts}/15`
                  : "Ainda não recebemos a confirmação. Se você já pagou, atualize a página em alguns instantes."}
              </span>
            </div>
            {data?.reservation && (
              <p className="text-sm text-white/50 mt-4 font-mono">
                Reserva: {data.reservation.id.slice(0, 8)}... · {data.reservation.model_name}
              </p>
            )}
          </>
        )}

        {status === "paid" && data && (
          <>
            <CheckCircle2 size={44} className="text-[color:var(--pm-neon)]" />
            <h1 className="font-display text-3xl font-black mt-4">Reserva confirmada!</h1>
            <p className="text-white/70 mt-3 leading-relaxed">
              Recebemos o sinal de <b className="text-white">{brl(data.amount)}</b> da sua reserva da{" "}
              <b className="text-white">{data.reservation?.model_name}</b>. Uma unidade das 30 do pré-lançamento é sua.
            </p>
            <div className="pm-card p-4 mt-6 bg-[color:var(--pm-surface-2)]">
              <div className="pm-label text-[10px]">Próximos passos</div>
              <ul className="mt-2 space-y-1 text-sm text-white/80">
                <li>1. Enviaremos o contrato de pré-lançamento por e-mail.</li>
                <li>2. Combinaremos os 80% restantes conforme o cronograma de produção.</li>
                <li>3. Entrega estimada em até 6 meses + 2 anos de melhorias.</li>
              </ul>
            </div>
            <a
              className="pm-btn-primary w-full mt-6 flex items-center justify-center gap-2"
              target="_blank" rel="noreferrer"
              href={WHATSAPP_LINK(
                `Olá! Acabei de pagar o sinal de ${brl(data.amount)} da ${data.reservation?.model_name}. Vamos combinar os próximos passos?`
              )}
              data-testid="success-whatsapp-button"
            >
              <MessageCircle size={16} /> Falar no WhatsApp
            </a>
            <button className="pm-btn-ghost w-full text-xs mt-3" onClick={() => navigate("/")} data-testid="success-home-button">
              Voltar para o site
            </button>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <XCircle size={44} className="text-red-400" />
            <h1 className="font-display text-2xl font-black mt-4">
              {status === "expired" ? "Sessão expirada" : "Ops, algo deu errado"}
            </h1>
            <p className="text-white/70 mt-3 text-sm">
              {status === "expired"
                ? "O prazo para concluir o pagamento passou e sua reserva foi liberada. Você pode tentar de novo."
                : "Não conseguimos verificar o status do seu pagamento. Se você já pagou, entre em contato pelo WhatsApp."}
            </p>
            <div className="flex gap-2 mt-6">
              <button className="pm-btn-ghost text-xs flex-1" onClick={() => navigate("/")} data-testid="failure-home-button">
                Voltar
              </button>
              <a
                className="pm-btn-primary text-xs flex-1 text-center"
                target="_blank" rel="noreferrer"
                href={WHATSAPP_LINK(`Olá! Tive um problema com o pagamento do sinal (${user?.email || ""}).`)}
              >
                WhatsApp
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
